/* global axios */
<script setup>
import { computed, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useUISettings } from 'dashboard/composables/useUISettings';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const store = useStore();
const currentAccountId = useMapGetter('getCurrentAccountId');
const { uiSettings } = useUISettings();

const incomingCall = ref(null);
const callerName = ref('');

const handleNewMessage = message => {
  // Only show notification for incoming WhatsApp voice calls that are ringing
  if (
    message.content_type === 'voice_call' &&
    message.message_type === 0 && // incoming
    message.content_attributes?.data?.status === 'ringing'
  ) {
    incomingCall.value = {
      ...message,
      callId: message.content_attributes.data.call_id,
    };

    // Try to get caller name from multiple sources
    if (message.sender?.name) {
      // First priority: message sender name (most reliable)
      callerName.value = message.sender.name;
    } else if (message.content_attributes?.data?.from) {
      // Second priority: phone number from call data
      callerName.value = message.content_attributes.data.from;
    } else {
      // Fallback to conversation meta
      const conversation = store.getters.getConversationById(
        message.conversation_id
      );
      callerName.value = conversation?.meta?.sender?.name || 'Unknown';
    }

    console.log('[WhatsApp Call] Incoming call from:', callerName.value);

    // Play ringtone if enabled in settings (default: true)
    const enableCallRingtone = uiSettings.value.enable_call_ringtone !== false;
    if (enableCallRingtone) {
      playRingtone();
    }
  }
};

const handleMessageUpdate = message => {
  if (!incomingCall.value || message.id !== incomingCall.value.id) {
    return;
  }

  console.log('[WhatsApp Call] Message update received:', {
    messageId: message.id,
    status: message.content_attributes?.data?.status,
    fullMessage: message
  });

  // Update the incomingCall object with new data (like room_id)
  incomingCall.value = {
    ...incomingCall.value,
    ...message,
  };

  // If the call status changed to no-answer or completed, hide notification
  const newStatus = message.content_attributes?.data?.status;
  console.log('[WhatsApp Call] Checking status:', newStatus, 'Should close:', newStatus !== 'ringing');

  if (newStatus !== 'ringing') {
    console.log('[WhatsApp Call] Closing popup due to status change');
    stopRingtone();
    incomingCall.value = null;
  }
};

const acceptCall = async () => {
  console.log('Accept WhatsApp call:', incomingCall.value.callId);
  stopRingtone();

  const callId = incomingCall.value.callId;
  const conversationId = incomingCall.value.conversation_id;
  const messageId = incomingCall.value.id;
  let webrtcOffer = incomingCall.value.content_attributes?.data?.webrtc_offer;

  // If webrtc_offer not available yet, wait briefly for backend to update message
  if (!webrtcOffer) {
    console.log('[WhatsApp Call] WebRTC offer not ready, waiting for backend...');

    // Wait up to 5 seconds for webrtc_offer to appear
    for (let i = 0; i < 10; i++) {
      await new Promise(resolve => setTimeout(resolve, 500));

      // Fetch fresh message from store
      const conversation = store.getters.getConversationById(conversationId);
      const freshMessage = conversation?.messages?.find(msg => msg.id === messageId);

      if (freshMessage?.content_attributes?.data?.webrtc_offer) {
        webrtcOffer = freshMessage.content_attributes.data.webrtc_offer;
        console.log('[WhatsApp Call] WebRTC offer received from store');
        break;
      }

      console.log(`[WhatsApp Call] Still waiting... (attempt ${i + 1}/10)`);
    }

    if (!webrtcOffer) {
      console.error('[WhatsApp Call] Timeout waiting for webrtc_offer from backend');
      alert('Call setup is taking longer than expected. Please try again in a moment.');
      return;
    }
  } else {
    console.log('[WhatsApp Call] Using WebRTC offer from backend');
  }

  // Set active call in Vuex store
  store.dispatch('whatsappCall/setActiveCall', {
    callId,
    conversationId,
    callerName: callerName.value,
    status: 'active',
    startTime: Date.now(),
  });

  // Clear the incoming notification
  incomingCall.value = null;

  // Setup WebRTC peer connection
  try {
    console.log('[WhatsApp Call] Setting up WebRTC connection...');

    // Create peer connection
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
    });

    // Store peer connection in Vuex for mute/unmute
    store.commit('whatsappCall/SET_JANUS_PLUGIN_HANDLE', pc);

    // Monitor connection state
    pc.oniceconnectionstatechange = () => {
      console.log('[WhatsApp Call] ICE connection state:', pc.iceConnectionState);
      if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
        console.error('[WhatsApp Call] ICE connection failed or disconnected');
      }
    };

    pc.onconnectionstatechange = () => {
      console.log('[WhatsApp Call] Connection state:', pc.connectionState);
      if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        console.error('[WhatsApp Call] Peer connection failed or disconnected');
      }
    };

    // Setup audio stream
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    console.log('[WhatsApp Call] Got local audio stream:', {
      id: stream.id,
      active: stream.active,
      tracks: stream.getTracks().map(t => ({ id: t.id, kind: t.kind, enabled: t.enabled, muted: t.muted }))
    });
    stream.getTracks().forEach(track => {
      const sender = pc.addTrack(track, stream);
      console.log('[WhatsApp Call] Added track to peer connection:', track.kind, track.id);
    });

    // Handle incoming audio
    let remoteAudio = null;
    pc.ontrack = (event) => {
      console.log('[WhatsApp Call] Received remote audio track:', {
        trackId: event.track.id,
        trackKind: event.track.kind,
        streamId: event.streams[0]?.id,
        trackEnabled: event.track.enabled,
        trackMuted: event.track.muted
      });

      if (!remoteAudio) {
        remoteAudio = new Audio();
        remoteAudio.autoplay = true;
      }

      remoteAudio.srcObject = event.streams[0];
      remoteAudio.play()
        .then(() => console.log('[WhatsApp Call] Remote audio playing'))
        .catch(err => console.error('[WhatsApp Call] Failed to play remote audio:', err));
    };

    // Set remote description (Janus's WebRTC offer)
    await pc.setRemoteDescription({
      type: 'offer',
      sdp: webrtcOffer
    });

    console.log('[WhatsApp Call] Remote description set');

    // Create answer
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);

    console.log('[WhatsApp Call] Waiting for ICE gathering to complete...');

    // CRITICAL FIX: Wait for ICE gathering to complete before sending SDP
    // This ensures all ICE candidates are included in the SDP answer
    await new Promise((resolve) => {
      if (pc.iceGatheringState === 'complete') {
        resolve();
      } else {
        const checkState = () => {
          console.log('[WhatsApp Call] ICE gathering state:', pc.iceGatheringState);
          if (pc.iceGatheringState === 'complete') {
            pc.removeEventListener('icegatheringstatechange', checkState);
            resolve();
          }
        };
        pc.addEventListener('icegatheringstatechange', checkState);

        // Fallback timeout after 5 seconds
        setTimeout(() => {
          console.log('[WhatsApp Call] ICE gathering timeout, proceeding anyway');
          pc.removeEventListener('icegatheringstatechange', checkState);
          resolve();
        }, 5000);
      }
    });

    console.log('[WhatsApp Call] ICE gathering complete, sending answer to backend...');

    // Send complete answer to backend → Janus → WhatsApp
    const accountId = currentAccountId.value;
    await axios.post(`/api/v1/accounts/${accountId}/whatsapp_calls/answer`, {
      call_id: callId,
      sdp_answer: pc.localDescription.sdp  // Use pc.localDescription which has complete ICE candidates
    });

    console.log('[WhatsApp Call] Successfully connected!');
  } catch (error) {
    console.error('[WhatsApp Call] Failed to setup WebRTC:', error);
    alert(`Call connection failed: ${error.message}`);
  }
};

const rejectCall = async () => {
  console.log('Reject WhatsApp call:', incomingCall.value.callId);
  stopRingtone();

  const callId = incomingCall.value.callId;

  // Clear the notification immediately for better UX
  incomingCall.value = null;

  try {
    // Send reject API call to WhatsApp
    const accountId = currentAccountId.value;
    await axios.post(`/api/v1/accounts/${accountId}/whatsapp_calls/reject`, {
      call_id: callId
    });

    console.log('[WhatsApp Call] Successfully rejected call');
  } catch (error) {
    console.error('[WhatsApp Call] Failed to reject call:', error);
    // Still show success to user since notification is already gone
  }
};

// Ringtone management
let ringtoneAudio = null;

const playRingtone = () => {
  try {
    // Use call ringtone
    ringtoneAudio = new Audio('/audio/dashboard/call-ring.mp3');
    ringtoneAudio.loop = true;
    ringtoneAudio.play().catch(err => {
      console.error('Failed to play ringtone:', err);
    });
  } catch (error) {
    console.error('Ringtone error:', error);
  }
};

const stopRingtone = () => {
  if (ringtoneAudio) {
    ringtoneAudio.pause();
    ringtoneAudio.currentTime = 0;
  }
};

// Watch for message updates in Vuex store (in case ActionCable event doesn't fire)
let messageWatcher = null;

watch(incomingCall, (newCall, oldCall) => {
  // Cleanup previous watcher
  if (messageWatcher) {
    messageWatcher();
    messageWatcher = null;
  }

  // If no incoming call, nothing to watch
  if (!newCall || !newCall.conversation_id || !newCall.id) return;

  // Watch the specific message in the store
  messageWatcher = watch(
    () => {
      const conversation = store.getters.getConversationById(newCall.conversation_id);
      return conversation?.messages?.find(msg => msg.id === newCall.id);
    },
    (updatedMessage) => {
      if (!updatedMessage) return;

      const status = updatedMessage.content_attributes?.data?.status;
      console.log('[WhatsApp Call] Store message updated:', {
        messageId: updatedMessage.id,
        status,
        content: updatedMessage.content
      });

      // Close popup if call is no longer ringing
      if (status && status !== 'ringing') {
        console.log('[WhatsApp Call] Closing popup due to store update (status changed to:', status, ')');
        incomingCall.value = null;
      }
    },
    { deep: true }
  );
});

// Listen for ActionCable events using emitter
useEmitter('newMessage', handleNewMessage);
useEmitter('message.updated', handleMessageUpdate);
</script>

<template>
  <transition name="slide-up">
    <div
      v-if="incomingCall"
      class="fixed bottom-4 right-4 z-50 bg-white rounded-lg shadow-2xl border border-n-slate-6 w-96 overflow-hidden"
    >
      <!-- Header -->
      <div class="bg-n-teal-9 text-white p-4 flex items-center gap-3">
        <div class="bg-white/20 rounded-full p-2 animate-pulse">
          <Icon icon="i-ph-phone-incoming" class="size-6" />
        </div>
        <div class="flex-1">
          <p class="text-sm font-medium">Incoming WhatsApp Call</p>
          <p class="text-xs opacity-90">{{ callerName }} is calling...</p>
        </div>
      </div>

      <!-- Body -->
      <div class="p-4 bg-n-slate-1">
        <div class="flex items-center gap-3 mb-4">
          <!-- Caller avatar -->
          <div class="size-12 rounded-full bg-n-teal-3 flex items-center justify-center text-n-teal-11 font-semibold text-lg">
            {{ callerName.charAt(0).toUpperCase() }}
          </div>
          <div class="flex-1">
            <p class="text-base font-semibold text-n-slate-12">{{ callerName }}</p>
            <p class="text-xs text-n-slate-11">WhatsApp Voice Call</p>
          </div>
        </div>

        <!-- Action buttons -->
        <div class="flex gap-3">
          <button
            class="flex-1 bg-n-ruby-9 hover:bg-n-ruby-10 text-white rounded-lg py-3 px-4 flex items-center justify-center gap-2 font-medium transition-colors"
            @click="rejectCall"
          >
            <Icon icon="i-ph-phone-x" class="size-5" />
            Decline
          </button>
          <button
            class="flex-1 bg-n-teal-9 hover:bg-n-teal-10 text-white rounded-lg py-3 px-4 flex items-center justify-center gap-2 font-medium transition-colors"
            @click="acceptCall"
          >
            <Icon icon="i-ph-phone-call" class="size-5" />
            Accept
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
